import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { RECAPTCHA_SITE_KEY } from "../config";

// Google reCAPTCHA v2 ("I'm not a robot" checkbox).
// The script is loaded once, the first time a form that needs it is shown.
const SCRIPT_URL = "https://www.google.com/recaptcha/api.js?onload=__onRecaptchaLoaded&render=explicit";
let scriptPromise = null;

function loadRecaptcha() {
    if (window.grecaptcha && window.grecaptcha.render) {
        return Promise.resolve(window.grecaptcha);
    }

    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            window.__onRecaptchaLoaded = () => resolve(window.grecaptcha);

            const script = document.createElement("script");
            script.src = SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onerror = () => {
                scriptPromise = null;
                script.remove();
                reject(new Error("Failed to load reCAPTCHA."));
            };
            document.head.appendChild(script);
        });
    }

    return scriptPromise;
}

/**
 * Props:
 *  - onChange(token): called with the token when the box is ticked,
 *    and with "" when the token expires or the widget is reset.
 *  - onLoadError(): called if the reCAPTCHA script could not be loaded.
 *
 * Ref: { reset() } - clears the tick. Call it after every submit,
 * because a token can only be verified once.
 *
 * Renders nothing when REACT_APP_RECAPTCHA_SITE_KEY is not set.
 */
const ReCaptcha = forwardRef(function ReCaptcha({ onChange, onLoadError }, ref) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const onLoadErrorRef = useRef(onLoadError);

    useEffect(() => {
        onChangeRef.current = onChange;
        onLoadErrorRef.current = onLoadError;
    });

    useImperativeHandle(ref, () => ({
        reset() {
            if (widgetIdRef.current !== null && window.grecaptcha) {
                window.grecaptcha.reset(widgetIdRef.current);
            }
            if (onChangeRef.current) {
                onChangeRef.current("");
            }
        },
    }));

    useEffect(() => {
        if (!RECAPTCHA_SITE_KEY || !containerRef.current) {
            return undefined;
        }

        let cancelled = false;
        // Render into a child element so the widget is never rendered twice
        // into the same node (React runs effects twice in development).
        const holder = document.createElement("div");
        containerRef.current.appendChild(holder);

        const notify = (token) => {
            if (onChangeRef.current) {
                onChangeRef.current(token);
            }
        };

        loadRecaptcha()
            .then((grecaptcha) => {
                if (cancelled) {
                    return;
                }
                widgetIdRef.current = grecaptcha.render(holder, {
                    sitekey: RECAPTCHA_SITE_KEY,
                    callback: (token) => notify(token),
                    "expired-callback": () => notify(""),
                    "error-callback": () => notify(""),
                });
            })
            .catch((error) => {
                console.log(error);
                if (!cancelled && onLoadErrorRef.current) {
                    onLoadErrorRef.current();
                }
            });

        return () => {
            cancelled = true;
            widgetIdRef.current = null;
            holder.remove();
        };
    }, []);

    if (!RECAPTCHA_SITE_KEY) {
        return null;
    }

    return <div ref={containerRef} className="recaptcha-container"></div>;
});

export default ReCaptcha;
